import { CommonForm } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonForm";
import { SignUpPill } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpPill";
import { useSignUpEventTracker } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IFaireDirectSignupAttributionRequest } from "@faire/web-api--source/indigofair/data/IFaireDirectSignupAttributionRequest";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { useFormikContext } from "formik";
import isUndefined from "lodash/isUndefined";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type FaireDirectSurveyData = Pick<
  ISignUpRequest,
  "faire_direct_signup_attribution"
>;

export const FaireDirectSurveyForm: React.FC = () => {
  const { values, isValid, setValues, handleSubmit } =
    useFormikContext<FaireDirectSurveyData>();
  // this state variable is used to track whether No has ever been pressed,
  // it states true once No is pressed and remains true for the rest of the signup
  const [hasAnsweredNo, setHasAnsweredNo] = useState(
    !!values?.faire_direct_signup_attribution
      ?.has_answered_not_purchased_from_brand
  );
  const [hasOrdered, setHasOrdered] = useState(
    values.faire_direct_signup_attribution?.have_purchased_from_brand
  );

  const { strictLocalize } = useStrictLocalization();
  const { trackFieldValue } = useSignUpEventTracker(Step.FAIRE_DIRECT_SURVEY);
  const [shouldAutoProgress] = useState(
    isUndefined(
      values.faire_direct_signup_attribution?.have_purchased_from_brand
    )
  );
  const delayedHandleSubmit = useDelayedCallback(handleSubmit);
  useEffect(() => {
    if (shouldAutoProgress && hasOrdered !== undefined) {
      delayedHandleSubmit(800);
    }
  }, [delayedHandleSubmit, shouldAutoProgress, handleSubmit, hasOrdered]);

  useEffect(() => {
    setValues({
      faire_direct_signup_attribution:
        IFaireDirectSignupAttributionRequest.build({
          have_purchased_from_brand: hasOrdered,
          has_answered_not_purchased_from_brand: hasAnsweredNo,
        }),
    });
  }, [hasAnsweredNo, hasOrdered, setValues]);

  const handleClick = (havePurchasedFromBrand: boolean) => {
    trackFieldValue(
      "have_purchased_from_brand",
      havePurchasedFromBrand.toString()
    );
    if (!havePurchasedFromBrand) {
      setHasAnsweredNo(true);
    }
    setHasOrdered(havePurchasedFromBrand);
  };
  return (
    <CommonForm>
      <Row gap={12}>
        <FitWidthPill
          title={strictLocalize({
            defaultMessage: "Yes, I have",
            description: {
              img: "https://cdn.faire.com/fastly/fc42519402a2e9488ab3dfd0cb02b39a956f987c1528cd9295a055b1a9e92369.png",
              text: "Button to indicate that the user has ordered from the brand before",
            },
          })}
          dataTestId="done-business"
          isSelected={!!hasOrdered}
          onClick={() => handleClick(true)}
        />
        <FitWidthPill
          title={strictLocalize({
            defaultMessage: "No, I haven’t",
            description: {
              img: "https://cdn.faire.com/fastly/fc42519402a2e9488ab3dfd0cb02b39a956f987c1528cd9295a055b1a9e92369.png",
              text: "Button to indicate that the user has not ordered from the brand before",
            },
          })}
          isSelected={!hasOrdered && hasOrdered !== undefined}
          onClick={() => handleClick(false)}
          dataTestId="no-business"
        />
      </Row>
      {!shouldAutoProgress ? (
        <>
          <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }} />
          <Button data-test-id="next" disabled={!isValid} type="submit">
            <StrictLocalMsg
              defaultMessage="Next"
              description={{ text: "Button to visit the next page" }}
            />
          </Button>
        </>
      ) : null}
    </CommonForm>
  );
};

export const useSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  return useMemo(
    () =>
      Yup.object().shape({
        faire_direct_signup_attribution: Yup.object().shape({
          have_purchased_from_brand: Yup.boolean().required(
            strictLocalize({
              defaultMessage: "Please select an option",
            })
          ),
        }),
      }),
    [strictLocalize]
  );
};

const FitWidthPill = styled(SignUpPill)`
  flex: 1;
`;
